import { SocialLogin } from '@capgo/capacitor-social-login';
import { ref } from 'vue';

export interface AppleLoginResponse {
  idToken: string;
  authorizationCode?: string;
  profile?: {
    email?: string;
    familyName?: string;
    givenName?: string;
    user: string;
  };
}

export interface AppleLoginError {
  message: string;
  code?: string;
}

const isInitialized = ref(false);

export function useAppleLogin() {
  const isLoggingIn = ref(false);
  const error = ref<AppleLoginError | null>(null);

  /**
   * Initialize the Apple Social Login plugin
   * This should be called once when the app starts
   */
  async function initialize(): Promise<void> {
    if (isInitialized.value) {
      console.log('[useAppleLogin] Already initialized');
      return;
    }

    try {
      console.log('[useAppleLogin] Initializing Apple Login');

      await SocialLogin.initialize({
        apple: {},
      });

      isInitialized.value = true;
      console.log('[useAppleLogin] Apple Login initialized successfully');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error during initialization';
      console.error('[useAppleLogin] Failed to initialize:', errorMessage);
      error.value = {
        message: errorMessage,
        code: 'INIT_ERROR',
      };
      throw err;
    }
  }

  /**
   * Trigger Apple login flow
   * @returns AppleLoginResponse with idToken and user profile
   */
  async function login(): Promise<AppleLoginResponse> {
    if (!isInitialized.value) {
      console.log('[useAppleLogin] Not initialized, initializing now...');
      try {
        await initialize();
      } catch {
        throw new Error('Failed to initialize Apple Login');
      }
    }

    isLoggingIn.value = true;
    error.value = null;

    try {
      console.log('[useAppleLogin] Starting Apple login flow');

      const result = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name'],
        },
      });

      console.log('[useAppleLogin] Login successful');
      console.log('[useAppleLogin] Full result:', JSON.stringify(result, null, 2));

      // Extract the identity token (JWT) from Apple's response
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const appleResult = result.result as any;

      // Try both possible field names (the plugin uses 'idToken', not 'identityToken')
      const idToken = appleResult?.idToken || appleResult?.identityToken;
      const authorizationCode = appleResult?.authorizationCode;
      const user = appleResult?.profile.user;

      console.log('[useAppleLogin] Extracted tokens:', {
        hasIdToken: !!idToken,
        hasAuthCode: !!authorizationCode,
        idTokenPreview: idToken ? idToken.substring(0, 50) + '...' : 'none',
      });

      if (!idToken) {
        console.error('[useAppleLogin] No idToken found in result:', appleResult);
        throw new Error('No identity token received from Apple');
      }

      // Build profile from Apple's response
      const profile = {
        email: appleResult?.profile?.email,
        familyName: appleResult?.profile?.familyName,
        givenName: appleResult?.profile?.givenName,
        user: user || '',
      };

      console.log('[useAppleLogin] Profile data:', {
        hasEmail: !!profile.email,
        hasName: !!(profile.givenName || profile.familyName),
        user: profile.user,
      });

      return {
        idToken,
        authorizationCode,
        profile,
      };
    } catch (err: unknown) {
      console.error('[useAppleLogin] Login error caught:', err);
      console.error('[useAppleLogin] Error type:', typeof err);
      console.error('[useAppleLogin] Error details:', JSON.stringify(err, null, 2));

      const errorMessage = err instanceof Error ? err.message : JSON.stringify(err) || 'Unknown error during login';
      console.error('[useAppleLogin] Processed error message:', errorMessage);

      error.value = {
        message: errorMessage,
        code: 'LOGIN_ERROR',
      };
      throw err;
    } finally {
      isLoggingIn.value = false;
    }
  }

  /**
   * Logout from Apple (if needed)
   */
  async function logout(): Promise<void> {
    if (!isInitialized.value) {
      console.log('[useAppleLogin] Not initialized, skipping logout');
      return;
    }

    try {
      await SocialLogin.logout({
        provider: 'apple',
      });
      console.log('[useAppleLogin] Logout successful');
    } catch (err) {
      console.error('[useAppleLogin] Logout error:', err);
    }
  }

  return {
    initialize,
    login,
    logout,
    isLoggingIn,
    isInitialized,
    error,
  };
}
